import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal, Dropdown } from 'react-bootstrap'

import { httpOk, POST, PUT, GET, DELETE } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'
import DeleteModal from 'pages/parts/DeleteModal'

function Index() {
	const { t } = useTranslation()

	const [data, setData] = useState([])
	const [deviceTypes, setDeviceTypes] = useState([])
	const [deviceModels, setDeviceModels] = useState([])
	const [selectedItem, setSelectedItem] = useState({})
	const [showModal, setShowModal] = useState(false)
	const [modalStep, setModalStep] = useState(1)
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteModalParams, setDeleteModalParams] = useState({ id: 0 })
	const SYSTEM_FIELD_KEYS = ['connection_type', 'port', 'address', 'horizontal_offset', 'tape_width', 'auto_start', 'print_spooler', 'is_sound_enabled']

	async function searchWithFilter() {
		var filterUrl = "/devices"

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	async function createUpdate(e) {
		e.preventDefault()

		var response;
		if (selectedItem.id) response = await PUT('/devices', selectedItem, { loader: true })
		if (!selectedItem.id) response = await POST('/devices', selectedItem, { loader: true })

		if (httpOk(response)) {
			searchWithFilter()
			toggleModal()
		}
	}

	async function deleteItem() {
		const response = await DELETE(`/devices/${deleteModalParams.id}`, {}, { loader: true })
		if (httpOk(response)) {
			setModalDelete(false)
			toggleModal()
			searchWithFilter()
		}
	}

	async function handleSelectDeviceType(option) {
		setSelectedItem({
			...selectedItem,
			'device_type_id': option ? option.id : null,
			'device_model_id': null,
			'integration_keys': null,
		})

		setDeviceModels([])
		if (option) getDeviceModels(option.id)
	}

	function handleSelectDeviceModel(option) {
		setSelectedItem({
			...selectedItem,
			'name': option?.name ?? selectedItem.name,
			'device_model_id': option ? option.id : null,
			'integration_keys': option?.integration_keys ?? {},
		})
	}

	function getSelectedDeviceModel() {
		return deviceModels.find(option => option.id === selectedItem.device_model_id)
	}

	function getFieldLabel(field) {
		return field.name || field.value || field.field_key
	}

	function getFieldDefaultValue(field) {
		if (field.default_value !== undefined && field.default_value !== null) return field.default_value
		if (field.input_type === 'checkbox') return false
		return ''
	}

	function getSelectedModelFields() {
		const selectedModel = getSelectedDeviceModel()
		if (selectedModel?.fields?.length) return selectedModel.fields

		return Object.keys(selectedModel?.integration_keys ?? {}).map((key, index) => ({
			'id': key,
			'field_key': key,
			'name': key,
			'value': key,
			'input_type': typeof selectedModel.integration_keys[key] === 'boolean' ? 'checkbox' : 'text',
			'is_required': false,
			'default_value': selectedModel.integration_keys[key],
			'position': index + 1,
		}))
	}

	function handleChangeIntegrationKey(e) {
		var selectedItemCopy = { ...selectedItem }
		selectedItemCopy.integration_keys[e.target.name] = e.target.value

		setSelectedItem(selectedItemCopy)
	}

	function getSelectedConnectionType() {
		return selectedItem.integration_keys?.connection_type || 'IP'
	}

	function isFieldVisible(field) {
		const connectionType = getSelectedConnectionType()

		if (field.field_key === 'address') {
			return connectionType === 'IP'
		}

		if (field.field_key === 'port') {
			return connectionType !== 'WINDOWS'
		}

		return true
	}

	function isFieldRequired(field) {
		if (!field.is_required) return false

		const connectionType = getSelectedConnectionType()

		if (field.field_key === 'address') {
			return connectionType === 'IP'
		}

		if (field.field_key === 'port') {
			return ['IP', 'LPT', 'COM'].includes(connectionType)
		}

		return true
	}

	function handleChangeConnectionKey(name, value) {
		const nextKeys = {
			...(selectedItem.integration_keys ?? {}),
			[name]: value,
		}

		if (name === 'connection_type' && value === 'WINDOWS') {
			nextKeys.address = ''
			nextKeys.port = ''
		}

		setSelectedItem({
			...selectedItem,
			'integration_keys': nextKeys,
		})
	}

	function openConnectionStep() {
		const fields = getSelectedModelFields()
		const fieldDefaults = {}
		fields.forEach((field) => {
			fieldDefaults[field.field_key] = getFieldDefaultValue(field)
		})

		setSelectedItem({
			...selectedItem,
			'integration_keys': {
				...fieldDefaults,
				...(selectedItem.integration_keys ?? {}),
			},
		})
		setModalStep(2)
	}

	function renderIntegrationField(field) {
		if (!isFieldVisible(field)) return null

		const fieldKey = field.field_key
		const inputType = field.input_type ?? 'text'
		const value = selectedItem.integration_keys?.[fieldKey] ?? getFieldDefaultValue(field)
		const options = Array.isArray(field.options) ? field.options : []
		const required = isFieldRequired(field)

		if (inputType === 'checkbox') {
			return (
				<label className="iiko-device-checkbox" key={fieldKey}>
					<input type="checkbox"
						checked={!!value}
						onChange={(e) => handleChangeConnectionKey(fieldKey, e.target.checked)} />
					<span>{getFieldLabel(field)}</span>
				</label>
			)
		}

		if (inputType === 'radio') {
			return (
				<div className="iiko-device-section" key={fieldKey}>
					<div className="iiko-device-section-title">
						{getFieldLabel(field)}
						{required && <span className="required-mark">*</span>}
					</div>
					<div className="iiko-device-connection-tabs">
						{options.map((option) => {
							const optionValue = typeof option === 'object' ? option.value : option
							const optionLabel = typeof option === 'object' ? (option.label || option.name || option.value) : option

							return (
								<label className="iiko-device-radio" key={optionValue}>
									<input
										type="radio"
										name={fieldKey}
										checked={value === optionValue}
										required={required}
										onChange={() => handleChangeConnectionKey(fieldKey, optionValue)}
									/>
									<span>{optionLabel}</span>
								</label>
							)
						})}
					</div>
				</div>
			)
		}

		return (
			<div className="iiko-device-param-row" key={fieldKey}>
				<label>
					{getFieldLabel(field)}
					{required && <span className="required-mark">*</span>}
				</label>
				<input
					type={inputType === 'password' ? 'password' : inputType === 'number' ? 'number' : 'text'}
					className="form-control"
					required={required}
					placeholder={field.placeholder ?? ''}
					value={value}
					onChange={(e) => handleChangeConnectionKey(fieldKey, e.target.value)}
				/>
			</div>
		)
	}

	async function toggleModal(bool = false, item = {}) {
		let nextItem = { ...item }

		if (bool && item.device_model_id) {
			const models = await getDeviceModels(item.device_type_id)
			nextItem = mergeIntegrationDefaults(nextItem, models)
		}

		setSelectedItem(nextItem)
		setModalStep(nextItem.id ? 2 : 1)
		setShowModal(bool)
	}

	function returnClassStatus(status) {
		if (status) return "bg-primary"
		if (!status) return "bg-danger"
	}

	async function getDeviceTypes() {
		const response = await GET('/helper/device-types')
		if (httpOk(response)) setDeviceTypes(response.data)
	}

	async function getDeviceModels(deviceTypeId) {
		const response = await GET(`/helper/device-models/${deviceTypeId}`, {}, { loader: true })
		if (httpOk(response)) {
			setDeviceModels(response.data)
			return response.data
		}
		return []
	}

	function mergeIntegrationDefaults(item, models = deviceModels) {
		const model = models.find((option) => option.id === item.device_model_id)
		if (!model?.fields?.length) return item

		const defaults = {}
		model.fields.forEach((field) => {
			defaults[field.field_key] = getFieldDefaultValue(field)
		})

		return {
			...item,
			integration_keys: {
				...defaults,
				...(item.integration_keys ?? {}),
			},
		}
	}

	useEffect(() => {
		getDeviceTypes()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('devices')} [{formatMoney(data?.data?.length)}]
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary"
						onClick={() => toggleModal(true)}>
						{t('create')}
					</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '15%' }}>{t('name2')}</th>
									<th style={{ 'width': '15%' }}>{t('devices')}</th>
									<th style={{ 'width': '15%' }}>{t('models')}</th>
									<th style={{ 'width': '40%' }}></th>
									<th style={{ 'width': '15%' }} className="text-center">{t('status')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{index + 1}</td>
										<td>
											<span className="link-primary" style={{ 'cursor': 'pointer' }}
												onClick={() => toggleModal(true, item)}>{item.name}</span>
										</td>
										<td>{item.device_type_name}</td>
										<td>{item.device_model_name}</td>
										<td></td>
										<td className={`text-center text-white ${returnClassStatus(item.status)}`}>
											{item.status &&
												<span>{t('activated')}</span>
											}
											{!item.status &&
												<span>{t('disconnected')}</span>
											}
										</td>
										<td>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														<div className="table-dropdown-item"
															onClick={() => toggleModal(true, item)}>
															<i className="uil-edit-alt"></i>
															{t('edit')}
														</div>
														<div className="table-dropdown-item text-danger"
															onClick={() => {
																setDeleteModalParams({ id: item.id })
																setModalDelete(true)
															}}>
															<i className="uil-trash-alt"></i>
															{t('delete')}
														</div>
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* MODAL */}
			<Modal
				show={showModal}
				animation={false}
				dialogClassName="update-modal-width iiko-device-modal"
				centered
				onHide={() => toggleModal()}>
				<Modal.Header closeButton>
					<Modal.Title>
						{selectedItem.id ?
							<>{t('edit')}</>
							:
							<>{t('create')}</>
						}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={createUpdate} autoComplete="off">
						{modalStep === 1 &&
							<>
								<div className="iiko-device-section">
									<div className="iiko-device-section-title">{t('type')}</div>
									<div className="iiko-device-type-grid">
										{deviceTypes.map((item) => (
											<label className="iiko-device-radio" key={item.id}>
												<input
													type="radio"
													name="device_type_id"
													checked={selectedItem.device_type_id === item.id}
													onChange={() => handleSelectDeviceType(item)}
												/>
												<span>{item.name}</span>
											</label>
										))}
									</div>
								</div>

								<div className="iiko-device-section">
									<div className="iiko-device-section-title">{t('models')}</div>
									<div className="iiko-device-model-list">
										{!selectedItem.device_type_id &&
											<div className="iiko-device-empty">Выберите тип устройства</div>
										}

										{selectedItem.device_type_id && !deviceModels.length &&
											<div className="iiko-device-empty">{t('list_empty')}</div>
										}

										{deviceModels.map((item) => (
											<button
												type="button"
												className={`iiko-device-model-item ${selectedItem.device_model_id === item.id ? 'active' : ''}`}
												key={item.id}
												onClick={() => handleSelectDeviceModel(item)}>
												{item.name}
											</button>
										))}
									</div>
								</div>

								<div className="d-flex gap-2 w-100 mt-3">
									<button type="button" className="btn btn-outline-warning w-100"
										onClick={() => toggleModal()}>
										{t('cancel')}
									</button>
									<button type="button" className="btn btn-primary w-100"
										disabled={!selectedItem.device_model_id}
										onClick={openConnectionStep}>
										Далее
									</button>
								</div>
							</>
						}

						{modalStep === 2 &&
							<>
								<div className="form-group">
									<label>{t('name2')}<span className="required-mark">*</span></label>
									<input type="text" className="form-control" name="name"
										autoFocus
										value={selectedItem.name ?? ''}
										onChange={(e) => setSelectedItem({ ...selectedItem, 'name': e.target.value })} />
								</div>

								<div className="iiko-device-summary">
									<div>
										<span>{t('type')}</span>
										<strong>{deviceTypes.find(option => option.id === selectedItem.device_type_id)?.name}</strong>
									</div>
									<div>
										<span>{t('models')}</span>
										<strong>{deviceModels.find(option => option.id === selectedItem.device_model_id)?.name}</strong>
									</div>
								</div>

								<div className="iiko-device-connection-box">
									{getSelectedModelFields().length > 0 ?
										getSelectedModelFields().map((field) => renderIntegrationField(field))
										:
										<div className="iiko-device-empty">{t('list_empty')}</div>
									}
								</div>

								{(selectedItem?.integration_keys && Object.keys(selectedItem?.integration_keys).filter(key => !SYSTEM_FIELD_KEYS.includes(key)).length > 0 && getSelectedModelFields().length === 0) &&
									<div className="iiko-device-extra-keys">
										{Object.keys(selectedItem?.integration_keys)
											.filter(key => !SYSTEM_FIELD_KEYS.includes(key))
											.map((key) => (
												<div className="form-group w-100" key={key}>
													<label>{key}</label>
													<input type="text" className="form-control" name={key}
														value={selectedItem?.integration_keys[key] ?? ''}
														onChange={handleChangeIntegrationKey} />
												</div>
											))}
									</div>
								}

								<div className="d-flex gap-2 w-100 mt-3">
									{selectedItem.id &&
										<button type="button" className="btn btn-outline-danger w-100"
											onClick={() => {
												setDeleteModalParams({ id: selectedItem.id })
												setModalDelete(true)
											}}>
											{t('delete')}
										</button>
									}
									{!selectedItem.id &&
										<button type="button" className="btn btn-outline-secondary w-100"
											onClick={() => setModalStep(1)}>
											Назад
										</button>
									}
									<button type="submit" className="btn btn-primary w-100">Завершить</button>
								</div>
							</>
						}
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL */}
			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}
			/>
		</>
	)
}

export default Index
